import { breakpoints, breakpointQueries, type Breakpoint } from '../styles/media';

import { useMatchMedia } from './useMatchMedia';

const BREAKPOINT_ORDER: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];
export type Span = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Builds a media query string for a breakpoint range
 * @param breakpoint - Starting breakpoint
 * @param spans - Number of breakpoints to span (default 1)
 * @returns Media query string
 */

export function buildBreakpointQuery(breakpoint: Breakpoint, spans: Span = 1): string {
    const startIndex = BREAKPOINT_ORDER.indexOf(breakpoint);

    const range = BREAKPOINT_ORDER.slice(startIndex, startIndex + spans);
    const last = range[range.length - 1];
    const next = BREAKPOINT_ORDER[BREAKPOINT_ORDER.indexOf(last) + 1];

    const minWidth = breakpoints[breakpoint];
    const maxWidth = next ? breakpoints[next] - 1 : undefined;

    if (!next) {
        return `screen and (min-width: ${minWidth}px)`;
    }

    if (minWidth === 0) {
        return `screen and (max-width: ${maxWidth}px)`;
    }

    return `screen and (min-width: ${minWidth}px) and (max-width: ${maxWidth}px)`;
}

/**
 * Mobile-first hook that returns true when viewport is at or above the specified breakpoint
 * @param breakpoint - The breakpoint name (sm, md, lg, xl)
 * @returns true if viewport width is at or above the breakpoint
 * @example
 * const isDesktop = useAbove('md'); // true for md, lg, xl
 */
export function useAbove(breakpoint: Breakpoint): boolean {
    const query = breakpointQueries[breakpoint];
    return useMatchMedia(query);
}

/**
 * Hook that returns true only when viewport is within the specified breakpoint range
 * @param breakpoint - The breakpoint name (xs, sm, md, lg, xl)
 * @param spans - Number of consecutive breakpoints to match (default 1)
 * @returns true if viewport width is within the breakpoint range
 * @example
 * const isMobile = useOnly('xs'); // true only for xs
 * const isTablet = useOnly('md'); // true only for md (768-991px)
 * const isMdOrLg = useOnly('md', 2); // true for md and lg (768-1279px)
 */
export function useOnly(breakpoint: Breakpoint, spans: Span = 1): boolean {
    const query = buildBreakpointQuery(breakpoint, spans);
    return useMatchMedia(query);
}

/**
 * Hook that returns true when viewport is below the specified breakpoint
 * @param breakpoint - The breakpoint name (sm, md, lg, xl)
 * @returns true if viewport width is below the breakpoint
 * @example
 * const isMobile = useBelow('md'); // true for xs and sm
 * const isTabletOrSmaller = useBelow('lg'); // true for xs, sm, md
 */
export function useBelow(breakpoint: Breakpoint): boolean {
    const maxWidth = breakpoints[breakpoint] - 1;
    const query = `screen and (max-width: ${maxWidth}px)`;

    return useMatchMedia(query);
}
